import { motion } from "framer-motion";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { scrollTo } from "@/App";

const FAQS = [
    {
        q: "How much does a dropped kerb cost?",
        a: "Every job is different — pavement width, surface type, and what's underneath (cables, pipes, drains) all affect the price. That's why we survey first and give you one fixed price that covers everything: council fees, excavation, materials and finish. No hourly rates, no extras later.",
    },
    {
        q: "Do I need council permission?",
        a: "Yes — dropping a kerb without an authorised vehicle crossing is illegal and can mean fines plus the cost of putting it back. The good news: we handle the entire application for you — forms, drawings, fees and chasing the council until it's approved.",
    },
    {
        q: "How long does the whole thing take?",
        a: "Council approval typically takes a few weeks depending on your local authority — we chase it so you don't have to. Once approved, we're usually on site for just two days, and you're driving over your new crossing the same week.",
    },
    {
        q: "Do I need planning permission for the driveway as well?",
        a: "Often not — permeable surfaces like gravel or permeable block paving usually don't need it. Larger non-permeable drives can. We check this during your free survey and handle any extra paperwork if it's needed.",
    },
    {
        q: "What if there's a lamppost, tree or utility cover in the way?",
        a: "We've seen it all. Part of the survey is checking for obstacles and utility services, and we arrange any protection, relocation or reinstatement needed — it's all included in your fixed quote.",
    },
    {
        q: "Are you insured, and is the work guaranteed?",
        a: "Fully insured with £5m public liability, and every crossing is built to council specification and guaranteed. The council inspects and signs off the work — we don't leave until it passes.",
    },
    {
        q: "Which areas do you cover?",
        a: "The whole of the Midlands and Lincolnshire — Birmingham, Coventry, Wolverhampton, Leicester, Nottingham, Derby, Lincoln and everywhere in between. Not sure? Call 07971 124 650 and ask.",
    },
];

export default function FaqSection() {
    return (
        <section id="faq" className="bg-concrete text-tarmac py-24 sm:py-32" data-testid="faq-section">
            <div className="px-5 sm:px-8 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-12">
                <div className="lg:sticky lg:top-24 self-start">
                    <motion.p
                        className="font-mono text-xs tracking-[0.25em] uppercase mb-4"
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.6 }}
                    >
                        [ Straight answers ]
                    </motion.p>
                    <motion.h2
                        className="font-display uppercase leading-[0.9] tracking-tight text-4xl sm:text-5xl lg:text-6xl mb-6"
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7 }}
                    >
                        Asked every<br />single day.
                    </motion.h2>
                    <motion.p
                        className="text-base text-tarmac/70 leading-relaxed max-w-sm mb-8"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                    >
                        Costs, councils and timescales — answered plainly. Anything else,
                        call <a href="tel:+447971124650" className="underline underline-offset-4 hover:text-amber transition-colors duration-200" data-testid="faq-call-link">07971 124 650</a>.
                    </motion.p>
                    <motion.button
                        data-testid="faq-quote-btn"
                        onClick={() => scrollTo("#quote")}
                        className="px-8 py-4 bg-tarmac text-paper font-mono text-xs tracking-[0.2em] uppercase font-bold hover:bg-amber hover:text-tarmac transition-colors duration-200"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7, delay: 0.15 }}
                    >
                        Get a free quote
                    </motion.button>
                </div>

                <motion.div
                    className="lg:col-span-2"
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.7, delay: 0.1 }}
                >
                    <Accordion type="single" collapsible className="border-t border-tarmac/20">
                        {FAQS.map((f, i) => (
                            <AccordionItem key={i} value={`faq-${i}`} className="border-b border-tarmac/20" data-testid={`faq-item-${i}`}>
                                <AccordionTrigger
                                    data-testid={`faq-trigger-${i}`}
                                    className="group py-6 hover:no-underline text-left gap-4 [&[data-state=open]>svg]:rotate-45 [&[data-state=open]>svg]:text-amber"
                                >
                                    <span className="flex items-baseline gap-4 sm:gap-6">
                                        <span className="font-mono text-xs text-tarmac/40 group-data-[state=open]:text-amber transition-colors duration-200">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        <span className="font-display uppercase tracking-tight text-lg sm:text-2xl leading-tight">
                                            {f.q}
                                        </span>
                                    </span>
                                </AccordionTrigger>
                                <AccordionContent className="text-base text-tarmac/70 leading-relaxed max-w-2xl pl-9 sm:pl-12" data-testid={`faq-content-${i}`}>
                                    {f.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </motion.div>
            </div>
        </section>
    );
}
